/**
 * 後端統一回應外層結構
 */
export interface ApiResponse<T> {
	code: number
	message: string
	data: T
}

/**
 * 頻道完整資訊
 */
export interface ChannelInfo {
	created_at: string
	refreshed_at: string
	expired_at: string
	code: string
	active_duration: number
	id: string
	languages: string[]
	male_tts_languages: string[]
	genders: string[]
	status: number
	prompt_tokens: number
	completion_tokens: number
	total_tokens: number
	total_connect: number
	max_connect: number
	capacity: number
	is_fgs: boolean
	title: string
	created_by: string
	is_on_air: boolean
	is_editable: boolean
	summary_state_message: string
	last_summary_created_at: string | null
	license: number
	is_tts_active?: boolean
}

/**
 * 語言分佈報表
 */
export interface LanguageReport {
	count: number
	languages: LanguageReportCell[]
}

/**
 * 語言分佈報表單列
 */
export interface LanguageReportCell {
	language: string
	count: number
	ratio: number
	avg_duration: number
}

/**
 * 觀眾活動報表
 */
export interface ActivityReport {
	count: number
	average_active_duration: number
	viewers: ActivityReportCell[]
}

/**
 * 觀眾活動報表單列
 */
export interface ActivityReportCell {
	viewer_id: string
	average_active_duration: number
	count: number
	active_times: ActivityDurationCell[]
	language_watch_duration: LanguageWatchDurationCell[]
}

/**
 * 觀眾單次活躍時間段
 */
export interface ActivityDurationCell {
	joined_at: string
	left_at: string | null
}

/**
 * 觀眾各語言觀看時數
 */
export interface LanguageWatchDurationCell {
	language: string
	duration: number
}

/**
 * Admin 頻道清單單列（/v1/admin/channels）
 */
export interface AdminChannelId {
	id: string
	code: string
	title: string
	languages: string[]
}

/**
 * 歷史訊息單列
 */
export interface channelHistoryCell {
	translated_text: Record<string, string>
	original_text: string
	speaker: string
	locale: string
	sent_at: number
	is_delete: boolean
	message_id: string
	file: string[]
}

/**
 * 頻道歷史訊息回傳
 */
export interface ChannelHistory {
	channel_id: string
	history: channelHistoryCell[]
	is_delete: boolean
}

/**
 * 評分結果報表
 */
export interface CommentedRating {
	count: number
	average_score: number
	commented_ratings: CommentedRatingCell[]
}

/**
 * 評分結果單列
 */
export interface CommentedRatingCell {
	score: number
	comment: string
}

/**
 * 摘要回傳
 */
export interface SummaryResponse {
	summary: string
	created_at: string
}

/**
 * 平台總覽卡片（A-1，/v1/admin/dashboard/overview）
 */
export interface DashboardOverview {
	total_channels: number
	channels_created_in_range: number
	total_active_minutes: number
	total_connect: number
	peak_concurrent_ever: number
	total_prompt_tokens: number
	total_completion_tokens: number
	total_tokens: number
}

/**
 * 頻道排行榜單列（A-2，/v1/admin/dashboard/channels）
 */
export interface DashboardChannelItem {
	channel_id: string
	code: string
	title: string
	created_by: string | null
	created_at: string | null
	device: string
	license: number
	languages: string[]
	total_connect: number
	max_connect: number
	active_duration: number
	prompt_tokens: number
	completion_tokens: number
	total_tokens: number
}

/**
 * 頻道排行榜回傳（A-2）
 */
export interface DashboardChannels {
	total: number
	items: DashboardChannelItem[]
}

/**
 * 頻道排行榜可排序欄位
 */
export type DashboardChannelSortBy =
	| 'total_connect'
	| 'max_connect'
	| 'active_duration'
	| 'total_tokens'
	| 'created_at'

/**
 * 頻道排行榜查詢參數（A-2）
 */
export interface DashboardChannelsQuery {
	sort_by?: DashboardChannelSortBy
	order?: 'asc' | 'desc'
	limit?: number
	offset?: number
	start_date?: string
	end_date?: string
}

/**
 * 時間趨勢單一時間桶（A-3）
 */
export interface DashboardTrendBucket {
	date: string
	new_channels: number
	active_seconds: number
	total_connect: number
}

/**
 * 時間趨勢回傳（A-3，/v1/admin/dashboard/trends）
 */
export interface DashboardTrends {
	granularity: string
	series: DashboardTrendBucket[]
}

/**
 * 單一語言供需併排列（依語言代號升冪排序）
 */
export interface LanguageOverviewItem {
	language: string
	channel_count: number
	viewer_count: number
}

/**
 * 語言供需總覽回傳（/v1/admin/dashboard/languages）
 * 取代原本的 supply / demand / gap 三隻；channel_count 為供給快照（不吃日期），viewer_count 為需求（吃日期區間）
 */
export interface LanguageOverviewResponse {
	items: LanguageOverviewItem[]
}

/**
 * 登入二次驗證請求（POST /v1/admin/login，token 放 body，不含 Bearer 前綴）
 */
export interface AdminLoginRequest {
	token: string
}

/**
 * 登入二次驗證回傳（POST /v1/admin/login）
 *
 * username 取自 MEB getProfileInfo 回應的 name.firstName + name.lastName，缺漏時後端已退回 user_id
 */
export interface AdminLoginResult {
	user_id: string
	username: string
}

/**
 * 2FA 綁定 QRCode 回傳（POST /v1/admin/2fa/setup）
 */
export interface TwoFaSetupResult {
	otpauth_url: string
}

/**
 * 2FA 驗碼請求（POST /v1/admin/2fa/verify）
 */
export interface TwoFaVerifyRequest {
	code: string
}

/**
 * 2FA 驗碼回傳（POST /v1/admin/2fa/verify）
 */
export interface TwoFaVerifyResult {
	/** 新發的正式 MEB token；暫時 token 已被 MEB 作廢，前端必須改用此 token */
	token: string
}

/**
 * 主講時長統計（GET /v1/admin/stats/speaker）
 */
export interface SpeakerStats {
	channel_ids: string[]
	code: string
	total_speaker_duration_seconds: number
}

/**
 * 觀眾語言統計單列（GET /v1/admin/stats/viewer 的 languages[]）
 */
export interface ViewerLanguageStat {
	language: string
	viewer_count: number
	ratio: number
	avg_duration_seconds: number
}

/**
 * 觀眾語言統計（GET /v1/admin/stats/viewer）
 */
export interface ViewerStats {
	languages: ViewerLanguageStat[]
	total: {
		viewer_count: number
		avg_duration_seconds: number
	}
}
